import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getImpersonationInfo, stopImpersonation } from '../../services/adminService';
import toast from 'react-hot-toast';

const ImpersonationBanner: React.FC = () => {
  const navigate = useNavigate();
  const [impersonation, setImpersonation] = useState(getImpersonationInfo());
  const [isRestoring, setIsRestoring] = useState(false);

  // Monitora alterações no localStorage
  useEffect(() => {
    const checkState = () => {
      setImpersonation(getImpersonationInfo());
    };

    window.addEventListener('storage', checkState);
    const interval = setInterval(checkState, 2000);

    return () => {
      window.removeEventListener('storage', checkState);
      clearInterval(interval);
    };
  }, []);

  if (!impersonation.isImpersonating) {
    return null;
  }

  const handleStop = async () => {
    setIsRestoring(true);
    const toastId = toast.loading('Restaurando sessão do Administrador...');

    try {
      const res = await stopImpersonation();
      if (res.success) {
        toast.success('Sessão de Administrador restaurada com sucesso!', { id: toastId });
        navigate('/admin');
        window.location.reload();
      } else {
        toast.error(res.error || 'Erro ao restaurar sessão.', { id: toastId });
        setIsRestoring(false);
      }
    } catch {
      toast.error('Erro ao encerrar modo de visualização.', { id: toastId });
      setIsRestoring(false);
    }
  };

  return (
    <motion.div
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -50, opacity: 0 }}
      className="sticky top-0 z-[100] bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white px-4 py-2.5 shadow-lg border-b border-amber-400/30 backdrop-blur-md"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm">
        {/* Info */}
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white shadow-inner flex-shrink-0">
            <span className="material-symbols-outlined text-lg animate-pulse">visibility</span>
          </span>
          <div>
            <p className="font-bold flex items-center gap-2">
              <span>Modo Administrador Ativo</span>
              <span className="bg-white/20 text-white text-[11px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider">
                Visão do Usuário
              </span>
            </p>
            <p className="text-amber-100 text-xs">
              Acessando como:{' '}
              <strong className="text-white underline">
                {impersonation.targetUserName || impersonation.targetUserEmail}
              </strong>{' '}
              ({impersonation.targetUserEmail})
            </p>
          </div>
        </div>

        {/* Botão de Retorno */}
        <motion.button
          onClick={handleStop}
          disabled={isRestoring}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="flex items-center gap-1.5 bg-gray-900/80 hover:bg-gray-950 text-white font-semibold px-4 py-1.5 rounded-lg shadow border border-white/20 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {isRestoring ? (
            <>
              <span className="material-symbols-outlined text-sm animate-spin">sync</span>
              <span>Restaurando...</span>
            </>
          ) : (
            <>
              <span className="material-symbols-outlined text-sm">admin_panel_settings</span>
              <span>Voltar para Painel Admin</span>
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ImpersonationBanner;
